// Справка „Дневник на резервациите" (Справки → reservations-journal).
//
// Един ред = една резервация с начало в периода: кога и за кого, маси, как е
// завършила (предстояща / настанена / приключила / отказана / неизпълнена),
// кой я е записал и настанил, закъснение, престой и сметката от настаняването.
// `GET /reports/analytics/reservations/journal` връща целия период наведнъж,
// затова таблицата е в КЛИЕНТСКИ режим.
//
// Денят е КАЛЕНДАРЕН, не работен: резервацията за 00:30 е в деня на своята
// дата (както в модула „Резервации"), а работният ден би я пратил в предния.
// Без „Δ спрямо предходния период" — endpoint-ът не сравнява два периода.

import { useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useReservationsJournalReport } from "../../../hooks/useReports";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import type { ReservationJournalRow } from "../../../api/reportsReservations";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { pluralLabel } from "./reportPlural";
import { JournalKpis } from "./reservations/JournalKpis";
import { useJournalColumns } from "./reservations/journalColumns";
import { exportJournal } from "./reservations/journalExport";
import { summarizeJournalRows } from "./reservations/journalMath";
import { useReservationPeriodFilters } from "./reservations/useReservationPeriodFilters";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const ReservationsJournalReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;

  const [dateRange, setDateRange] = useReportsDateParam(() => [
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [searchText, setSearchText] = useState("");
  const [visibleRows, setVisibleRows] = useState<ReservationJournalRow[]>([]);

  const filters = useReservationPeriodFilters(dateRange);
  const { data, isLoading, isFetching, refetch } =
    useReservationsJournalReport(filters);
  const rows = useMemo(() => data?.rows ?? [], [data]);

  // Търсачката гледа това, което човек знае за резервацията: гост, телефон,
  // маса, бележка, кой я е записал. Статусът има собствен филтър в колоната.
  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [
        row.guest_name,
        row.phone,
        row.table_names.join(" "),
        row.note ?? "",
        row.created_by_name ?? "",
        row.first_order_by_name ?? "",
        row.bill_name ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [rows, searchText]);

  // Кутийките следват ТЪРСАЧКАТА, както таблицата и Excel.
  const summary = useMemo(
    () => summarizeJournalRows(searchedRows),
    [searchedRows],
  );

  const columns = useJournalColumns();

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.rvJournalKicker")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleRows.length,
          "reportsPage.rvReservationOne",
          "reportsPage.rvReservations",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.rvJournalHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.rvJournal")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
            />
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <JournalKpis summary={summary} isNarrow={narrow} />

              <DataTable<ReservationJournalRow>
                tableId="reservations-journal"
                data={searchedRows}
                columns={columns}
                getRowId={(row) => String(row.id)}
                refreshing={isFetching}
                ariaLabel={t("reportsPage.rvJournal")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.rvJournalSearchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={() => exportJournal({ rows: visibleRows, t })}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                // Хронологично, както в календара: първата резервация на деня горе.
                initialSorting={[{ id: "start_at", desc: false }]}
                emptyTitle={t("reportsPage.rvNoData")}
                emptyHint={t("reportsPage.rvEmptyHint")}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
